import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';

import { filterProps } from 'utils/helpers';

import PushToTalkButton from './PushToTalkButton';
import { TranscribedText } from './TranscribedText';
import { usePushToTalkWorkspaceService } from './hooks';
import SuggestionChips from 'components/molecules/SuggestionChips';
import { useSelector, useDispatch } from 'react-redux';
import selectors from 'features/Chat/selectors';
import actions from 'features/Chat/actions';

interface IPushToTalkWorkspace {
  onSendMessage?: (message: string) => void;
}

const Container = styled('div', filterProps('position', 'hasTopPadding'))<{
  position: 'fixed' | 'absolute';
  hasTopPadding: boolean;
}>`
  ${({ theme: { colors, mq }, position, hasTopPadding }) => css`
    position: ${position};
    display: grid;
    justify-items: center;
    align-items: center;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: ${colors.neutral.white};
    border-radius: 4rem 4rem 0 0;
    padding: ${hasTopPadding ? 3 : 0}rem 3rem 3rem;
    box-shadow: 0px -1.5rem 2.5rem 0px rgba(43, 20, 59, 0.04);
    z-index: 5;
    overflow: visible;

    ${mq.md.down} {
      border-radius: 3rem 3rem 0 0;
    }
  `}
`;

const SuggestionChipsContainer = styled('div', filterProps('position'))<{
  position: 'fixed' | 'absolute';
}>`
  ${({ theme: { mq }, position }) => css`
    position: ${position};
    bottom: 18rem; /* Position higher above the white container */
    left: 0;
    right: 0;
    padding: 1rem 0; /* Vertical padding only for shadows */
    z-index: 4;
    overflow: visible;

    ${mq.md.down} {
      bottom: 16rem;
      padding: 1rem 0;
    }
  `}
`;

const Wrapper = styled('div', filterProps('position'))<{
  position: 'fixed' | 'absolute';
}>`
  ${({ position }) => css`
    position: ${position};
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 5;
    overflow: visible;
  `}
`;

const PushToTalkWorkspace: React.FC<IPushToTalkWorkspace> = ({ onSendMessage }) => {
  const { shouldRender, pushToTalkActive, isOnboarding } = usePushToTalkWorkspaceService();
  const suggestionChips = useSelector(selectors.getSuggestionChips);

  const handleChipClick = (chip: string) => {
    if (onSendMessage) {
      onSendMessage(chip);
    }
  };

  const position = isOnboarding ? 'absolute' : 'fixed' as const;

  return shouldRender ? (
    <Wrapper position={position}>
      {suggestionChips && suggestionChips.responses.length > 0 && (
        <SuggestionChipsContainer position={position}>
          <SuggestionChips
            chips={suggestionChips.responses}
            onChipClick={handleChipClick}
          />
        </SuggestionChipsContainer>
      )}
      <Container position={position} hasTopPadding={pushToTalkActive}>
        <TranscribedText />
        {pushToTalkActive ? <PushToTalkButton /> : null}
      </Container>
    </Wrapper>
  ) : null;
};

export default PushToTalkWorkspace;
